<template>
  <dx-modal
    id="table-conflicts-modal"
    title="Serviços em mais de uma tabela"
    size="md"
    :value="true"
    @input="cancel"
  >
    <div class="conflicts-intro">
      <span class="conflicts-eyebrow">REVISÃO DE PREÇOS</span>
      <p>
        {{ items.length }} {{ items.length === 1 ? 'serviço aparece' : 'serviços aparecem' }}
        em mais de uma tabela deste plano. O plano recebe um único valor por serviço:
        escolha qual tabela vale.
      </p>
    </div>

    <div class="conflicts-list">
      <div class="conflicts-header">
        <span class="conflict-table">Tabela</span>
        <span class="conflict-value">Convênio</span>
        <span class="conflict-value">Particular</span>
      </div>
      <div class="conflict-row" v-for="item in items" :key="item.expense.id">
        <div class="conflict-name">{{ item.expense.name }}</div>
        <label
          class="form-radio conflict-option"
          v-for="table in item.tables"
          :key="table.id"
        >
          <input
            type="radio"
            :name="`conflict-${item.expense.id}`"
            :value="table.id"
            v-model="choices[item.expense.id]"
          >
          <i class="form-icon"/>
          <span class="conflict-table">{{ table.name }}</span>
          <span class="conflict-value">{{ table.insuranceValue | currency }}</span>
          <span class="conflict-value">{{ table.particularValue | currency }}</span>
        </label>
      </div>
    </div>

    <label class="form-checkbox conflicts-remove">
      <input type="checkbox" v-model="removeDuplicates">
      <i class="form-icon"/> Também excluir o serviço das tabelas que não foram escolhidas
    </label>
    <div class="conflicts-hint">
      Opcional. Sem marcar, nada é excluído: só este plano passa a usar a tabela escolhida.
    </div>

    <div class="conflicts-impact" v-if="removeDuplicates">
      <div class="impact-title">Ao continuar, serão excluídos:</div>
      <div class="impact-list">
        <div class="impact-row" v-for="impact in impacts" :key="impact.id">
          <div class="impact-head">
            <span class="impact-table">Da tabela {{ impact.name }}</span>
            <span class="impact-count">{{ serviceCount(impact.services.length) }}</span>
          </div>
          <div class="impact-services">
            {{ impact.services.slice(0, 5).join(', ') }}<span
              v-if="impact.services.length > 5"> e mais {{ impact.services.length - 5 }}</span>
          </div>
          <div class="impact-plans text-error" v-if="impact.losing.length">
            Ficam sem preço em: {{ impact.losing.join(', ') }}
          </div>
          <div class="impact-plans" v-if="impact.keeping.length">
            Mantêm o preço por outra tabela: {{ impact.keeping.join(', ') }}
          </div>
          <div class="impact-plans" v-if="!impact.losing.length && !impact.keeping.length">
            Tabela usada só por este plano
          </div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <button class="btn btn-primary mr-1" @click="confirm">Continuar</button>
      <button class="btn" @click="cancel">Cancelar</button>
    </template>
  </dx-modal>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      removeDuplicates: false,
      choices: this.items.reduce((result, item) => {
        const chosen = item.tables.find(table => table.chosen) || item.tables[0];
        return { ...result, [item.expense.id]: chosen.id };
      }, {}),
    };
  },
  computed: {
    impacts() {
      const byTable = {};

      this.items.forEach((item) => {
        const removedTableIds = item.tables
          .map(table => table.id)
          .filter(id => id !== this.choices[item.expense.id]);

        item.tables
          .filter(table => removedTableIds.includes(table.id))
          .forEach((table) => {
            const impact = byTable[table.id] || {
              id: table.id,
              name: table.name,
              services: [],
              losing: new Set(),
              keeping: new Set(),
            };
            impact.services.push(item.expense.name);
            (table.plans || []).forEach((plan) => {
              const keepsPrice = (plan.expenseTableIds || [])
                .some(id => !removedTableIds.includes(id));
              impact[keepsPrice ? 'keeping' : 'losing'].add(plan.name);
            });
            byTable[table.id] = impact;
          });
      });

      return Object.values(byTable)
        .map(impact => ({
          ...impact,
          losing: [...impact.losing],
          keeping: [...impact.keeping].filter(name => !impact.losing.has(name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    serviceCount(total) {
      return `${total} ${total === 1 ? 'serviço' : 'serviços'}`;
    },
    confirm() {
      this.$emit('confirm', {
        choices: Object.keys(this.choices).map(expenseId => ({
          expenseId,
          tableId: this.choices[expenseId],
        })),
        removeDuplicates: this.removeDuplicates,
      });
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
@import '../../../../assets/scss/variables';

#table-conflicts-modal {
  .conflicts-intro {
    margin-bottom: $layout-spacing;
    p {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
    }
  }
  .conflicts-eyebrow {
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: .05rem;
  }
  .conflicts-list {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    max-height: 18rem;
    overflow-y: auto;
  }
  .conflict-row {
    padding: $layout-spacing-sm $layout-spacing;
    & + .conflict-row {
      border-top: $border-width solid $border-color;
    }
  }
  .conflict-name {
    font-weight: 600;
    margin-bottom: .1rem;
  }
  .conflict-option {
    align-items: center;
    display: flex;
    margin: 0;
  }
  .conflicts-header {
    background-color: $gray-color-ultra-light;
    border-bottom: $border-width solid $border-color;
    color: $gray-color-dark;
    display: flex;
    font-size: $font-size-xs;
    font-weight: 600;
    padding: .2rem $layout-spacing;
    position: sticky;
    text-transform: uppercase;
    top: 0;
    z-index: 1;
    .conflict-table {
      padding-left: 1.2rem;
    }
  }
  .conflict-table {
    flex: 1;
    min-width: 0;
  }
  .conflict-value {
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
    width: 6rem;
  }
  .conflict-option .conflict-value {
    color: $gray-color-dark;
  }
  .conflicts-remove {
    margin: $layout-spacing 0 0;
  }
  .conflicts-hint {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    padding-left: 1.2rem;
  }
  .conflicts-impact {
    background-color: #FFF8E6;
    border: $border-width solid #F2C94C;
    border-radius: $border-radius;
    font-size: $font-size-sm;
    margin-top: $layout-spacing-sm;
    padding: $layout-spacing-sm $layout-spacing;
    .impact-title {
      font-weight: 600;
      margin-bottom: $layout-spacing-sm;
    }
    .impact-list {
      background-color: $light-color;
      border: $border-width solid #F5DD8E;
      border-radius: $border-radius;
      max-height: 12rem;
      overflow-y: auto;
    }
    .impact-row {
      padding: $layout-spacing-sm $layout-spacing;
      & + .impact-row {
        border-top: $border-width solid $border-color;
      }
    }
    .impact-head {
      align-items: baseline;
      display: flex;
      justify-content: space-between;
    }
    .impact-table {
      font-weight: 600;
    }
    .impact-count {
      color: $gray-color-dark;
      font-size: $font-size-xs;
      white-space: nowrap;
    }
    .impact-services {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
    .impact-plans {
      font-size: $font-size-xs;
      margin-top: .1rem;
    }
  }
}
</style>
